import WebsiteBehaviors from "../website-behaviors/index.js";
import {injectStyles, removeElement, triggerEvent} from "../utils/document.js";
import {waitUntil} from "../utils/async.js";
import Selection from "../selection.js";

class Lexis extends WebsiteBehaviors {
	identifier() {
		return "Lexis";
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static test(location) {
		// We don't really support "Advance" anymore, as most people (including us)
		// have moved over to "Plus", but we can leave it in here on the off-chance
		// that it helps somebody...
		return (location.host === "advance.lexis.com" || location.host === "plus.lexis.com");
	}

	styles() {
		// language=CSS
		return `
			.highlighted {
				background-color: white !important;
			}
			.lx-annotation-anchor {
				display: none !important;
			}
			.lx-highlight-anchor {
				display: none !important;
			}
			.selectedtextmenu {
				display: none !important;
			}
		`;
	}

	async init() {
		await super.init();

		// Get permalink
		await new Promise((r) => setTimeout(r, 150));

		// Simulate a "click" on the "Link to This Page" button
		const permalinkButton = document.querySelector("button.permalink");
		triggerEvent(document, permalinkButton, "click");

		const styles = `
			ln-document-title ladialog {
				display: none !important;
			}
		`;
		const stylesRef = injectStyles(document, styles);

		const stopCondition = () => document.getElementById("inputtextbox");
		try {
			await waitUntil(stopCondition, 200, 3000);

			const permalinkInput = document.getElementById("inputtextbox");
			const permalink = (permalinkInput as any).value;

			document.documentElement.setAttribute("data-pn-url", permalink);
		} catch (e) {
			// The element never showed up in the DOM, so we have no permalink.
			// We still need to close the permalink dialog (below), and do some style cleanup.
		}

		setTimeout(function () {
			triggerEvent(document, ".permalink-dialog-box-header .la-CloseRemove", "click");
		}, 250);

		setTimeout(() => removeElement(document, stylesRef), 500);
	}

	getCopyButtonLabel() {
		return "Copy With Citation";
	}

	/**
	 * On this site, we just open their "copy selected text to clipboard" modal.
	 */
	// @ts-expect-error TS(2416) FIXME: Property 'copySelectionToClipboard' in type 'Lexis... Remove this comment to see the full error message
	async copySelectionToClipboard(selectedText, document, loadCitation) {
		const buttons = document.querySelectorAll("button[data-id=copyselectedtext]");
		if (!buttons.length)
			// Didn't find the button, let's fall back to copying the text to the clipboard immediately (i.e. what we usually do).
			return super.copySelectionToClipboard(selectedText, document, loadCitation);

		const lastButton = Array.from(buttons).pop();
		triggerEvent(document, lastButton, "click");
		return false;
	}

	isCitationStructured() {
		return false;
	}

	async generateFreeformCitation(style: string): Promise<{style?: string; citation: string}> {
		// style parameter is ignored here, and must be.
		// See mouseUp(), where we capture citation data because the text selection
		// doesn't exist anymore by the time generateFreeformCitation() is called.
		if ((this as any).capturedCitation) {
			const cit = (this as any).capturedCitation;
			delete (this as any).capturedCitation;
			return cit; // return the data captured from mouseUp()
		}

		// Add some CSS to hide the Lexis popup
		// language=CSS
		const styles = `
			ln-document-copycitationdialog, #busy, #popup {
				display: none !important;
			}
		`;
		const stylesRef = injectStyles(document, styles);

		// Simulate a "click" on the "Copy (Advanced)" button
		const copyButtons = document.querySelectorAll("button[data-id=copyselectedtext]");
		// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		const copyAdvancedButton = Array.from(copyButtons).find(button => button.textContent.includes("Advanced"));

		// If we move on too quickly, we get "Cannot read property 'parentNode' of null",
		// but I have no idea what element is missing, so we'll just pause a bit.
		// 50 ms works reliably on my machine, so how about 150 to be safe?
		await new Promise((r) => setTimeout(r, 150));

		triggerEvent(document, copyAdvancedButton, "click");

		let citationHtml = null;
		let citationStyle = null;

		const stopCondition = () => document.getElementById("ClipboardText");
		try {
			await waitUntil(stopCondition, 200, 3000);

			const citationElement = document.querySelector("#ClipboardText .previewtextStyle");
			if (citationElement)
				citationHtml = citationElement.innerHTML.trim();

			const styleElement = document.getElementById("CurrentStyleFormat");
			if (styleElement)
			// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
				citationStyle = "Lexis - " + styleElement.textContent.trim();
		} catch (e) {
			// The element never showed up in the DOM, so we have no citation.
			// We still need to close the citation window (below), and do some style cleanup.
		}

		setTimeout(function () {
			triggerEvent(document, "#citationCloseButton", "click");
		}, 250);

		setTimeout(() => removeElement(document, stylesRef), 500);

		if (!citationHtml)
			throw new Error("PN: Could not get citation data");

		return citationStyle ? {citation: citationHtml, style: citationStyle} : {citation: citationHtml};
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
	async mouseUp(event, bypassHighlightRules, isSelectionEqual, isPageCoordEqual, page, processOrgProxy) {
		const result = await super.mouseUp(event, bypassHighlightRules, isSelectionEqual, isPageCoordEqual, page, processOrgProxy);

		if (result) { // valid selection for a highlight
			const selection = Selection.GetCurrentSelection();

			try {
				// Wait for Lexis popup to render.
				const stopCondition = () => {
					const copyButtons = document.querySelectorAll("button[data-id=copyselectedtext]");
					// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
					const copyAdvancedButton = Array.from(copyButtons).find(button => button.textContent.includes("Advanced"));
					return copyAdvancedButton;
				};
				await waitUntil(stopCondition, 50, 3000);

				// Grab the citation for later. (See notes in generateFreeformCitation().)
				// @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
				(this as any).capturedCitation = await this.generateFreeformCitation();
			} catch (e) {
				console.warn("PN: Lexis: Failed to capture citation data", e);
			}

			// I think Lexis' code clears the selection after getting the citation,
			// so we manually recover it from our saved copy.
			Selection.RecoverSavedSelection(selection);
		}

		return result;
	}
}

export default Lexis;
